import React from 'react';
import {
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  Paper,
  Popper
} from '@material-ui/core';
import classNames from 'classnames';
import Draggable from 'react-draggable';
import { ResizableBox } from 'react-resizable';
import 'react-resizable/css/styles.css'; // main css file

import './DraggableDialog.scss';
import Icon from '../Icon/Icon';

interface DraggableDialogProps {
  /**
   * A HTML element used to set the position of the popper.
   *
   * @default document.body
   */
  anchorEl?: HTMLElement,
  /**
   * Class to combine with draggable-dialog div.
   */
  className?: string,
  /**
   * Whether or not dialog can be resized
   *
   * @default false
   */
  canResize?: boolean,
  /**
   * Children nodes.
   */
  children: React.ReactNode,
  /**
   * Disable the portal behavior
   *
   * @default false
   */
  disablePortal?: boolean,
  /**
   * Where it should be placed
   *
   * @default botom
   */
  placement?: 'bottom-end' | 'bottom-start' |
  'bottom' | 'left-end' | 'left-start' | 'left' | 'right-end' | 'right-start' | 'right'
  | 'top-end' | 'top-start' | 'top'
}

interface DialogTitleProps {
  /**
   * Function called when dialog is closed.
   */
  onClose: () => void,
  /**
   * Children nodes.
   */
  children: React.ReactNode
}

interface DialogContentProps {
  /**
   * Children nodes.
   */
  children: React.ReactNode,
  /**
   * Display the top and bottom dividers.
   *
   * @default false
   */
  dividers?: boolean
}

function DialogTitle({ children, onClose, ...other }: DialogTitleProps) {
  return (
    <MuiDialogTitle className="draggable-dialog__title" disableTypography { ...other }>
      <div className="drag-handle">
        { children }
      </div>
      {
        onClose && (
          <Icon icon="close" className="draggable-dialog__close" onClick={ onClose } />
        )
      }
    </MuiDialogTitle>
  );
}

function DialogContent({ children, dividers = false, ...other }: DialogContentProps) {
  return (
    <MuiDialogContent className="draggable-dialog__content" dividers={ dividers } { ...other }>
      { children }
    </MuiDialogContent>
  );
}

const CustomResizeHandle = React.forwardRef((props: any, ref) => {
  const { handleAxis, canResize, ...other } = props;

  return (
    <div
      className={ `draggable-dialog__resize-handle draggable-dialog__resize-handle--${ handleAxis }` }
      ref={ ref }
      { ...other }
    >
      { canResize && <Icon icon="arrow" className="draggable-dialog__resize-handle-icon" /> }
    </div>
  );
});
CustomResizeHandle.displayName = 'CustomResizeHandle';

export default function DraggableDialog({
  anchorEl = document.body,
  className = '',
  canResize = false,
  children,
  disablePortal=false,
  placement='bottom'
}: DraggableDialogProps) {
  return (
    <Popper
      className="draggable-dialog-container"
      open={ true }
      disablePortal={ disablePortal }
      anchorEl={ anchorEl }
      placement={ placement }
      modifiers={
        {
          preventOverflow: {
            enabled: true,
            boundariesElement: 'scrollParent'
          }
        }
      }
    >
      <Draggable handle=".drag-handle">
        <ResizableBox
          axis={ canResize ? 'both' : 'none' }
          width={ 350 }
          height={ 300 }
          minConstraints={ [350, 300] }
          maxConstraints={ [700, 600] }
          handle={ <CustomResizeHandle canResize={ canResize } /> }
        >
          <div className={ classNames('draggable-dialog', className, { 'draggable-dialog--resizable': canResize }) }>
            <Paper>
              { children }
            </Paper>
          </div>
        </ResizableBox>
      </Draggable>
    </Popper>
  );
}

DraggableDialog.Title = DialogTitle;
DraggableDialog.Content = DialogContent;
